import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Gemini-style smooth streaming hook
 * - Character-by-character streaming to prevent UTF-8 corruption
 * - Natural speed variation
 * - requestAnimationFrame-based animation
 */
export const useSmoothStreaming = (options = {}) => {
  const {
    charDelay = 8,           // Base delay between characters (ms)
    minDelay = 2,            // Minimum delay
    maxDelay = 20,           // Maximum delay (for punctuation etc.)
    smoothness = 0.95,       // Smoothness level (0-1)
  } = options;

  const [displayText, setDisplayText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);

  const fullTextRef = useRef('');
  const currentIndexRef = useRef(0);
  const animationFrameRef = useRef(null);
  const lastTimeRef = useRef(0);
  const velocityRef = useRef(1);

  // Calculate per-character delay
  const getCharDelay = useCallback((char, nextChar) => {
    // Long pause after period, exclamation, question mark
    if (['.', '!', '?'].includes(char) && nextChar === ' ') {
      return maxDelay * 2;
    }

    // General punctuation
    if ([',', '、', '·', ':', ';'].includes(char)) {
      return maxDelay * 1.2;
    }

    // Short pause for spaces
    if (char === ' ') {
      return minDelay * 1.5;
    }

    // Line break
    if (char === '\n') {
      return maxDelay * 1.5;
    }

    // Numbers and special characters slightly faster
    if (/[0-9\-\+\=\(\)]/.test(char)) {
      return charDelay * 0.7;
    }

    // Regular characters - slight randomness
    const variation = 0.8 + Math.random() * 0.4;
    return charDelay * variation;
  }, [charDelay, minDelay, maxDelay]);

  // Smooth animation loop
  const animate = useCallback((timestamp) => {
    if (!lastTimeRef.current) {
      lastTimeRef.current = timestamp;
    }

    const deltaTime = timestamp - lastTimeRef.current;
    const targetLength = fullTextRef.current.length;

    if (currentIndexRef.current < targetLength) {
      const currentChar = fullTextRef.current[currentIndexRef.current];
      const nextChar = fullTextRef.current[currentIndexRef.current + 1];
      const delay = getCharDelay(currentChar, nextChar);

      // Velocity smoothing
      const targetVelocity = 1000 / delay;
      velocityRef.current = velocityRef.current * smoothness + targetVelocity * (1 - smoothness);

      const effectiveDelay = 1000 / velocityRef.current;

      if (deltaTime >= effectiveDelay) {
        // Append next character
        currentIndexRef.current++;
        const newText = fullTextRef.current.substring(0, currentIndexRef.current);

        setDisplayText(newText);
        lastTimeRef.current = timestamp;
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      // Streaming complete
      setIsStreaming(false);
      lastTimeRef.current = 0;
    }
  }, [getCharDelay, smoothness]);

  // Set text and start streaming
  const startStreaming = useCallback((text) => {
    if (!text) return;

    // Stop existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Start with new text
    fullTextRef.current = text;
    currentIndexRef.current = 0;
    velocityRef.current = 1;
    lastTimeRef.current = 0;
    setDisplayText('');
    setIsStreaming(true);

    // Start animation
    animationFrameRef.current = requestAnimationFrame(animate);
  }, [animate]);

  // Append text (incremental streaming) - core method
  const appendText = useCallback((chunk) => {
    if (!chunk) return;

    // Accumulate chunk into full text
    fullTextRef.current += chunk;

    // Start streaming if not already running
    if (!isStreaming && fullTextRef.current.length > 0) {
      setIsStreaming(true);
      animationFrameRef.current = requestAnimationFrame(animate);
    }
  }, [isStreaming, animate]);

  // Immediately complete (show all remaining text)
  const finish = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    setDisplayText(fullTextRef.current);
    currentIndexRef.current = fullTextRef.current.length;
    setIsStreaming(false);
  }, []);

  // Reset
  const reset = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    fullTextRef.current = '';
    currentIndexRef.current = 0;
    velocityRef.current = 1;
    lastTimeRef.current = 0;
    setDisplayText('');
    setIsStreaming(false);
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return {
    displayText,
    isStreaming,
    startStreaming,
    appendText,
    finish,
    reset,
  };
};

export default useSmoothStreaming;
